/**
 * The product window's standard look (the Prints mockup's print window: `.pw`,
 * `.pw-card`, `.pw-info`, `.seg`, `.sizes`, `.size`, `.pw-price`, `.pw-ship`,
 * `.pw-add`, `.pw-close` and `.pw-nav`), in theme tokens: a dialog over the
 * page with the product's picture on the left where the mockup draws Dana's
 * wall (her own drawing), the choices on the right; on a phone the picture on
 * top and Add to bag held at the bottom. Nothing while no product is open.
 * Portalled to the body, so no page layout clips or stacks over it. A product
 * placed by its handle is the same card drawn where it is placed, with no
 * frame, and nothing while it loads or when the shop does not show it. Draws
 * the behavior half's state and calls its actions.
 */
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { shownOptionLabel } from '@telify/shared/shop/line-label';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { PrintPicture } from '../../ui/PrintPicture';
import { contract } from './contract';
import type { ShopProductRuntime } from './useShopProduct';
import css from './ShopProduct.module.css';

const part = partsOf(contract);

function Choices({ state, actions, titleId }: ShopProductRuntime & { readonly titleId: string }) {
  if (state.status === 'missing') return <p className={css.missing}>This item isn&rsquo;t available any more.</p>;
  if (state.status !== 'ready' || !state.picked) return null;
  // shop-self-fulfilment D2: a product's name is said once, so a size or label that only repeats it is left out.
  const sizes = state.sizes.filter((s) => shownOptionLabel(state.title, s.label) !== null);
  const pickedLabel = shownOptionLabel(state.title, state.picked.label);
  return (
    <>
      <div className={css.stage} {...part('image')}>
        {state.image && <PrintPicture className={css.picture} url={state.image.url} alt={state.image.alt} size={state.image.size} canvas={state.formats.find((f) => f.selected)?.format === 'canvas'} />}
      </div>
      <div className={css.info}>
        <h3 id={titleId} className={css.title} {...part('title')}>
          {state.title}
        </h3>
        {state.description && (
          <p className={css.from} {...part('description')}>
            {state.description}
          </p>
        )}
        {state.formats.length > 1 && (
          <div className={css.seg} role="group" aria-label="Format" {...part('formats')}>
            {state.formats.map((f) => (
              <button
                key={f.label}
                type="button"
                aria-pressed={f.selected}
                onClick={() => actions.pickOption(f.optionId)}
                {...part('format', f.selected && 'selected')}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        {sizes.length > 0 && (
          <div className={css.sizes} role="radiogroup" aria-label="Size" {...part('sizes')}>
            {sizes.map((s) => (
              <button
                key={s.optionId}
                type="button"
                role="radio"
                aria-checked={s.selected}
                className={css.size}
                onClick={() => actions.pickOption(s.optionId)}
                {...part('size', s.selected && 'selected')}
              >
                <span className={css.d}>{s.label}</span>
                <span className={css.p}>{s.price}</span>
              </button>
            ))}
          </div>
        )}
        <div className={css.price} {...part('price')}>
          <b>{state.picked.price}</b>
          {pickedLabel && <span>{pickedLabel}</span>}
        </div>
        <p className={css.ship} {...part('shipping')}>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3 6h11v10H3zM14 9h4l3 3v4h-7" />
            <circle cx="7" cy="17.5" r="1.8" />
            <circle cx="17.5" cy="17.5" r="1.8" />
          </svg>
          <span>{state.shipping}</span>
        </p>
        {state.full && (
          <p className={css.full} role="alert" {...part('full')}>
            {state.full}
          </p>
        )}
        <div className={css.add}>
          <button
            type="button"
            className={`${shared.btn} ${shared.btnPrimary} ${css.wide}`}
            disabled={state.adding === 'added' || state.soldOut}
            onClick={actions.addToBag}
            {...part('addToBag')}
          >
            {state.soldOut ? 'Sold out' : state.adding === 'added' ? 'Added to your bag' : `Add to bag · ${state.picked.price}`}
          </button>
        </div>
        {state.position && (
          <div className={css.nav}>
            <button type="button" className={css.round} aria-label="Previous item" onClick={actions.previous} {...part('previous')}>
              <Icon name="left" small />
            </button>
            <span {...part('position')}>{state.position}</span>
            <button type="button" className={css.round} aria-label="Next item" onClick={actions.next} {...part('next')}>
              <Icon name="right" small />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Window(runtime: ShopProductRuntime) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const { close } = runtime.actions;

  // The window takes the focus while it is open and gives it back to what opened it.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLElement>('[data-part="close"]')?.focus({ preventScroll: true });
    return () => opener?.focus({ preventScroll: true });
  }, []);

  return createPortal(
    <div
      ref={dialog}
      className={css.pw}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(e) => e.target === e.currentTarget && close()}
      {...pieceRoot(contract)}
    >
      <div className={css.frame}>
        <button type="button" className={`${css.round} ${css.close}`} aria-label="Close" onClick={close} {...part('close')}>
          <Icon name="close" small />
        </button>
        <div className={css.card} {...part('window')}>
          <Choices {...runtime} titleId={titleId} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** A product placed by its handle, drawn where it is placed. */
function Placed(runtime: ShopProductRuntime) {
  const titleId = useId();
  if (runtime.state.status !== 'ready') return null;
  return (
    <section className={`${css.card} ${css.inline}`} aria-labelledby={titleId} {...pieceRoot(contract)} {...part('window')}>
      <Choices {...runtime} titleId={titleId} />
    </section>
  );
}

export function ShopProductView(runtime: ShopProductRuntime) {
  if (runtime.options.product !== null) return <Placed {...runtime} />;
  return runtime.state.status === 'closed' ? null : <Window {...runtime} />;
}
